import React, {memo, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import {useAppKit, useAppKitAccount, useAppKitNetwork} from "@reown/appkit/react";
import classNames from "classnames";
import {ethers} from "ethers";

import {CHAIN_TO_PARAMS_MAP, CURRENT_CHAINS_ENVIRONMENT, CURRENT_SUPPORTED_NETWORKS, NETWORK_ID} from "web3/chains";
import {
    CHAIN_ID,
    INTERACTION,
    GLOBAL_MODALS,
    kompetePriceCalculationBaseChain,
    kompetePriceCalculationEthChain
} from "utils";
import {useGetBazaarInventoryQuery} from "store/Global/global.api";
import {ModalsProvider, useGlobalModalsContext} from "layouts";
import {ActiveItemProvider} from "./useActiveItemContext";
import {globalService} from "store/Global/globalService";
import {CHAIN_TO_ADDRESSES_MAP} from "web3/addresses";
import {useActionContext} from "./useActionContext";
import {KOMPETEGameAssetABI} from "web3/ABI";
import {useAuthorization} from "hooks";

import BazaarCurrentItemInteractionTools from "./BazaarCurrentItemInteractionTools";
import BazaarCurrentItemOffersListings from "./BazaarCurrentItemOffersListings";
import BazaarCurrentItemTransactions from "./BazaarCurrentItemTransactions";
import BazaarCurrentItemPriceHistory from "./BazaarCurrentItemPriceHistory";
import BazaarCurrentItemModals from "./modals/BazaarCurrentItemModals";
import BazaarCurrentItemOrigin from "./BazaarCurrentItemOrigin";
import {Image, Tag, Retired} from "components/atoms";
import {Share} from "components/molecules";

import "../style.scss"

const BazaarCurrentItemContainer = () => {
    const {address, network, token_id} = useParams()
    const {chainId: currentChainId} = useAppKitNetwork()
    const {isConnected} = useAppKitAccount()
    const {setModal} = useGlobalModalsContext()
    const {actionDetails} = useActionContext()
    const {isAuth} = useAuthorization()
    const {open} = useAppKit()

    const isFirstRender = useRef(true);
    const navigate = useNavigate()

    const chainId = NETWORK_ID[network] ?? CHAIN_ID.BASE_MAINNET
    
    const {userData} = useSelector(state => state.authV2.signIn)
    const [kompeteWethPrice, setKompeteWethPrice] = useState(null)
    const [ethPrice, setEthPrice] = useState(null)
    const [userNftBalance, setUserNftBalance] = useState(null)

    const {data, isLoading, isSuccess, isError, isFetching, refetch} = useGetBazaarInventoryQuery({token_id, chainId})

    const imageBlockStyle = classNames("bazaar_current-item_container_head_left-block_img", {skeleton: isLoading}, `background-${data?.inventory.rarity.name}`)
    const nameStyle = classNames({skeleton: isLoading})
    const attributesStyle = classNames("bazaar_current-item_container_head_right-block_item-details_attributes", {skeleton: isLoading})

    useEffect(() => {
        globalService.getEthPrice().then(price => setEthPrice(price))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        checkParams()
        getKompetePrice()
        followChainUpdate()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChainId, isError]);

    useEffect(() => {
        handleActionDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionDetails]);

    useEffect(() => {
        getUserCurrentNFTBalance().then((result) => setUserNftBalance(result))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChainId, isAuth]);

    const getKompetePrice = () => {
        switch (chainId) {
            case CHAIN_ID.ETH_MAINNET:
            case CHAIN_ID.ETH_SEPOLIA:
                kompetePriceCalculationEthChain().then((oneKompeteWETH) => {
                    setKompeteWethPrice(oneKompeteWETH)
                })
                    .catch(() => setKompeteWethPrice(0))
                break
            case CHAIN_ID.BASE_MAINNET:
            case CHAIN_ID.BASE_SEPOLIA:
                kompetePriceCalculationBaseChain().then((oneKompeteWETH) => {
                    setKompeteWethPrice(oneKompeteWETH)
                })
                    .catch(() => setKompeteWethPrice(0))
                break
            default:
                setKompeteWethPrice(0)
        }
    }

    const checkParams = () => {
        const isChainIdSupported = () => CURRENT_SUPPORTED_NETWORKS[CURRENT_CHAINS_ENVIRONMENT].includes(network)
        const isAddressSupported = () => CHAIN_TO_ADDRESSES_MAP[chainId].COLLECTION_ADDRESS.toLocaleLowerCase() === address.toLocaleLowerCase()

        if (!isChainIdSupported() || !isAddressSupported() || isError) navigate("/bazaar")
    }

    const followChainUpdate = () => {
        if (isError) return;

        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const {network, addresses} = CHAIN_TO_PARAMS_MAP[currentChainId]

        navigate(`/bazaar/inventory/${network}/${addresses.COLLECTION_ADDRESS}/${token_id}`)
    }

    const calculateKompetePrice = (kompetePrice) => {
        const oneKompetePrciceUSD = ethPrice * kompeteWethPrice
        return `$${(oneKompetePrciceUSD * kompetePrice).toFixed(3)}`
    }

    const getUserCurrentNFTBalance = async () => {
        if (isAuth) {
            const {addresses, rpcURL} = CHAIN_TO_PARAMS_MAP[currentChainId]

            const provider = new ethers.providers.JsonRpcProvider(rpcURL)
            const contract = new ethers.Contract(addresses.COLLECTION_ADDRESS, KOMPETEGameAssetABI, provider)

            try {
                const balance = await contract.balanceOf(userData.wallet, token_id);

                return Number(balance.toString());
            } catch {
                return 0
            }
        }

        return null
    }

    const handleAuthAndWalletChecks =  () => {
        if (!isAuth) {
            setModal(GLOBAL_MODALS.LOGIN_ALERT);
            return false;
        }

        if (!isConnected) {
            open();
            return false;
        }

        return true;
    };

    const handleActionDetails = () => {
        if ((actionDetails.type === INTERACTION.BUY) && actionDetails.isSuccess) {
            refetch()
        }

        if ((actionDetails.type === INTERACTION.ACCEPT_OFFER || actionDetails.type === INTERACTION.BUY) && actionDetails.isSuccess) {
            setUserNftBalance(null)
            getUserCurrentNFTBalance().then((result) => setUserNftBalance(result))
        }
    }

    const isInProgress = isLoading || isFetching || data === undefined

    return (
        <ModalsProvider>
            <ActiveItemProvider
                value={{
                    data,
                    isError,
                    chainId,
                    network,
                    ethPrice,
                    isSuccess,
                    isInProgress,
                    kompeteWethPrice,
                    calculateKompetePrice,
                    handleAuthAndWalletChecks
                }}>
                <div className="bazaar_current-item_container">
                    <div className="bazaar_current-item_container_head">
                        <div className="bazaar_current-item_container_head_left-block">
                            <div className={imageBlockStyle}>
                                <Image alt="cosmetic" src={data?.inventory.image_url ?? null}/>
                            </div>
                        </div>
                        <div className="bazaar_current-item_container_head_right-block">
                            <div className="bazaar_current-item_container_head_right-block_item-details">
                                <div className="bazaar_current-item_container_head_right-block_item-details_title">
                                    <h2 className={nameStyle}>{data?.inventory.name}</h2>
                                    <Share/>
                                </div>
                                <div className={attributesStyle}>
                                    {isSuccess &&
                                        <React.Fragment>
                                            <Tag text={data.inventory.rarity.name} rarity={data.inventory.rarity.name}/>
                                            {data?.inventory.groups.map((item, index) =>
                                                <React.Fragment key={index}>
                                                    <Tag text={item.name} rarity="default"/>
                                                    {item?.parent &&
                                                        <Tag text={item?.parent.value} rarity="default"/>}
                                                </React.Fragment>
                                            )}
                                            {data?.inventory.never_sold_again && <Retired/>}
                                        </React.Fragment>}
                                </div>
                                <BazaarCurrentItemInteractionTools userNftBalance={userNftBalance}/>
                            </div>
                        </div>
                    </div>
                    <div className="bazaar_current-item_container_sections">
                        <BazaarCurrentItemOrigin details={data?.origin}/>
                        <BazaarCurrentItemPriceHistory/>
                        <BazaarCurrentItemTransactions/>
                        <BazaarCurrentItemOffersListings data={data} userNftBalance={userNftBalance}/>
                    </div>
                </div>
                <BazaarCurrentItemModals/>
            </ActiveItemProvider>
        </ModalsProvider>
    )
}

export default memo(BazaarCurrentItemContainer)